import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CaretRight,
  DownloadSimple,
  MagnifyingGlass,
  Plus,
  Stack,
  Trash,
} from "phosphor-react";
import { Form, Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import Loader from "../CustomCommons/Loader";
import { showToast } from "../../utils/ToastHelper";
import GcPagination from "./GcPagination";
import BulkActivateModal from "./BulkActivateModal";
import {
  ACTIVATABLE,
  GC_API,
  StatusPill,
  dateTime,
  downloadXlsx,
  isSuperAdmin,
  money,
  showApiError,
} from "./giftCardCommon";
import "./css/giftcard.css";

const LIMIT = 20;
const COLUMNS = 12;

const GiftCardBatchList = () => {
  const history = useHistory();
  const canDelete = isSuperAdmin();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [skuQuery, setSkuQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  // Expanded batch row: the cards it generated, fetched once and cached per batch.
  const [expandedId, setExpandedId] = useState(null);
  const [cardsByBatch, setCardsByBatch] = useState({});
  const [cardsLoadingId, setCardsLoadingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteCardTarget, setDeleteCardTarget] = useState(null);
  const [deletingCard, setDeletingCard] = useState(false);
  // Only one batch is ever expanded, so one selection set is enough - it's
  // reset whenever the expanded batch changes.
  const [selectedCardIds, setSelectedCardIds] = useState(new Set());
  const [showBulkActivate, setShowBulkActivate] = useState(false);
  const [form, setForm] = useState({
    sku: "",
    amount: "",
    selling_price: "",
    quantity: "",
    prefix: "",
    note: "",
  });

  const loadUrl = (url) => {
    setIsLoading(true);
    setExpandedId(null);
    axios
      .get(url)
      .then((res) => {
        setTotal(res.data?.data?.count || 0);
        setItems(res.data?.data?.results || []);
        setNextUrl(res.data?.data?.next);
        setPrevUrl(res.data?.data?.previous);
      })
      .catch((err) => showApiError(err, "Could not load batches"))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setPageNumber(1);
    loadUrl(`${GC_API}admin/batch/?sku=${encodeURIComponent(skuQuery)}`);
  }, [skuQuery]);

  const reloadCards = (batchId) => {
    setCardsLoadingId(batchId);
    axios
      .get(`${GC_API}admin/batch/${batchId}/cards/`)
      .then((res) =>
        setCardsByBatch((prev) => ({
          ...prev,
          [batchId]: res.data?.data?.results || [],
        }))
      )
      .catch((err) => showApiError(err, "Could not load the cards"))
      .finally(() => setCardsLoadingId(null));
  };

  const toggleExpand = (batchId) => {
    setSelectedCardIds(new Set());
    if (expandedId === batchId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(batchId);
    if (cardsByBatch[batchId]) return;
    reloadCards(batchId);
  };

  const deleteBatch = () => {
    setDeleting(true);
    axios
      .post(`${GC_API}admin/batch/${deleteTarget?.id}/soft-delete/`)
      .then((res) => {
        const data = res.data?.data ?? res.data ?? {};
        showToast("success", data.detail || `Batch #${deleteTarget?.id} deleted`);
        setDeleteTarget(null);
        loadUrl(`${GC_API}admin/batch/?sku=${encodeURIComponent(skuQuery)}`);
      })
      .catch((err) => {
        // Already gone (bad id or already hidden) - the row just disappears.
        if (err?.request?.status === 404) {
          showToast("success", "That batch is already gone");
          setDeleteTarget(null);
          loadUrl(`${GC_API}admin/batch/?sku=${encodeURIComponent(skuQuery)}`);
          return;
        }
        showApiError(err, "Could not delete the batch");
      })
      .finally(() => setDeleting(false));
  };

  const deleteCard = () => {
    setDeletingCard(true);
    // toggleExpand skips refetching when a batch's cards are already cached,
    // so a deleted card would keep showing on re-expand until this clears.
    const batchId = expandedId;
    const dropCache = () => {
      if (!batchId) return;
      setCardsByBatch((prev) => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
    };
    axios
      .post(`${GC_API}admin/card/${deleteCardTarget?.id}/soft-delete/`)
      .then((res) => {
        const data = res.data?.data ?? res.data ?? {};
        showToast(
          "success",
          data.detail || `Card #${deleteCardTarget?.id} deleted`
        );
        setDeleteCardTarget(null);
        dropCache();
        loadUrl(`${GC_API}admin/batch/?sku=${encodeURIComponent(skuQuery)}`);
      })
      .catch((err) => {
        // Already gone (bad id or already hidden) - the row just disappears.
        if (err?.request?.status === 404) {
          showToast("success", "That card is already gone");
          setDeleteCardTarget(null);
          dropCache();
          loadUrl(`${GC_API}admin/batch/?sku=${encodeURIComponent(skuQuery)}`);
          return;
        }
        showApiError(err, "Could not delete the card");
      })
      .finally(() => setDeletingCard(false));
  };

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const createBatch = () => {
    if (!form.sku.trim()) return showToast("error", "SKU is required");
    if (!(Number(form.amount) > 0))
      return showToast("error", "Amount must be greater than 0");
    if (!(Number(form.quantity) >= 1))
      return showToast("error", "Quantity must be at least 1");
    if (form.selling_price && !(Number(form.selling_price) > 0))
      return showToast("error", "Selling price must be greater than 0");

    // Each card renders and uploads a barcode, so this is slow - keep the button locked.
    setSaving(true);
    const payload = {
      sku: form.sku,
      amount: form.amount,
      quantity: Number(form.quantity),
    };
    if (form.selling_price) payload.selling_price = form.selling_price;
    if (form.prefix) payload.prefix = form.prefix;
    if (form.note) payload.note = form.note;

    axios
      .post(`${GC_API}admin/batch/`, payload)
      .then((res) => {
        showToast(
          "success",
          `${res.data?.data?.generated || 0} gift card(s) generated`
        );
        setShowForm(false);
        setForm({
          sku: "",
          amount: "",
          selling_price: "",
          quantity: "",
          prefix: "",
          note: "",
        });
        loadUrl(`${GC_API}admin/batch/?sku=${encodeURIComponent(skuQuery)}`);
      })
      .catch((err) => showApiError(err, "Could not generate the batch"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="categorey-parent gc-page">
      <div className="gc-head">
        <div>
          <h3>Gift Card Batches</h3>
          <p className="gc-sub">
            Every gift card in circulation comes from a batch generated here.
          </p>
        </div>
        <div className="gc-actions">
          <button className="gc-btn" onClick={() => setShowForm(true)}>
            <Plus size={17} weight="bold" />
            Generate Cards
          </button>
        </div>
      </div>

      <div className="gc-panel">
        <div className="gc-panel__bar">
          <div className="gc-field gc-field--icon" style={{ maxWidth: "320px" }}>
            <span className="gc-field__icon">
              <MagnifyingGlass size={17} />
            </span>
            <input
              className="gc-input"
              type="text"
              placeholder="Filter by SKU"
              aria-label="Filter batches by SKU"
              onChange={(e) => setSkuQuery(e.target.value)}
            />
          </div>
        </div>

        <div className={`gc-scroll ${isLoading ? "gc-scroll--loading" : ""}`}>
          <table className="gc-table">
            <thead>
              <tr>
                <th style={{ width: "46px" }} aria-label="Expand" />
                <th style={{ width: "90px" }}>Serial No.</th>
                <th style={{ width: "160px" }}>Batch Code</th>
                <th style={{ width: "180px" }}>Created Date</th>
                <th style={{ width: "130px" }}>Amount</th>
                <th style={{ width: "130px" }}>Cards Quantity</th>
                <th style={{ width: "140px" }}>Generated Cards</th>
                <th style={{ width: "110px" }}>Sold Cards</th>
                <th style={{ width: "130px" }}>Redeemed Cards</th>
                <th style={{ width: "110px" }}>Left Cards</th>
                <th style={{ width: "190px" }}>Reason</th>
                <th style={{ width: "300px", textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : items.length > 0 ? (
                items.map((batch, index) => {
                  const open = expandedId === batch?.id;
                  const cards = cardsByBatch[batch?.id] || [];
                  // Serial runs across pages, not per page.
                  const serial = (pageNumber - 1) * LIMIT + index + 1;
                  // No left-count from the server - generated minus sold.
                  const leftCards =
                    batch?.total_left ??
                    (batch?.generated_count ?? 0) - (batch?.total_sold ?? 0);
                  return (
                    <React.Fragment key={batch?.id}>
                      <tr
                        className={`gc-row gc-row--clickable ${
                          open ? "gc-row--open" : ""
                        }`}
                        onClick={() => toggleExpand(batch?.id)}
                      >
                        <td>
                          <span
                            className={`gc-caret ${open ? "gc-caret--open" : ""}`}
                          >
                            <CaretRight size={15} weight="bold" />
                          </span>
                        </td>
                        <td className="gc-num gc-strong">{serial}</td>
                        <td className="gc-code">{batch?.code || "—"}</td>
                        <td className="gc-num">{dateTime(batch?.created_at)}</td>
                        <td className="gc-num gc-strong">
                          {money(batch?.amount)}
                        </td>
                        <td className="gc-num">{batch?.quantity ?? "—"}</td>
                        <td className="gc-num">{batch?.generated_count}</td>
                        <td className="gc-num">{batch?.total_sold ?? 0}</td>
                        <td className="gc-num">{batch?.total_redeemed ?? 0}</td>
                        <td className="gc-num">{leftCards}</td>
                        <td>{batch?.note || "—"}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="gc-cell-actions">
                            <button
                              className="gc-btn gc-btn--ghost gc-btn--sm"
                              onClick={() =>
                                history.push(
                                  `/gift-card/card-list?sku=${encodeURIComponent(
                                    batch?.sku || ""
                                  )}`
                                )
                              }
                            >
                              View Cards
                            </button>
                            <button
                              className="gc-btn gc-btn--ghost gc-btn--sm"
                              onClick={() =>
                                downloadXlsx(
                                  axios,
                                  `${GC_API}admin/batch/${batch?.id}/export/`,
                                  `gift-card-batch-${batch?.id}.xlsx`
                                )
                              }
                            >
                              <DownloadSimple size={15} weight="bold" />
                              Export
                            </button>
                            <button
                              className="gc-btn gc-btn--danger gc-btn--sm"
                              disabled={!canDelete}
                              title={
                                canDelete
                                  ? undefined
                                  : "Only Super Admin can delete a batch"
                              }
                              onClick={() => setDeleteTarget(batch)}
                            >
                              <Trash size={15} weight="bold" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>

                      {open && (
                        <tr>
                          <td className="gc-expand" colSpan={COLUMNS}>
                            <div className="gc-expand__inner">
                              {cardsLoadingId === batch?.id ? (
                                <div className="gc-loading">
                                  <Loader />
                                </div>
                              ) : (
                                <>
                                  <p className="gc-expand__title">
                                    Cards in batch #{batch?.id}
                                  </p>
                                  {(() => {
                                    const selectable = cards.filter((c) =>
                                      ACTIVATABLE(c?.status)
                                    );
                                    const allSelected =
                                      selectable.length > 0 &&
                                      selectable.every((c) =>
                                        selectedCardIds.has(c.id)
                                      );
                                    const toggleOneCard = (id) =>
                                      setSelectedCardIds((prev) => {
                                        const next = new Set(prev);
                                        next.has(id)
                                          ? next.delete(id)
                                          : next.add(id);
                                        return next;
                                      });
                                    const toggleAllCards = () =>
                                      setSelectedCardIds(
                                        allSelected
                                          ? new Set()
                                          : new Set(selectable.map((c) => c.id))
                                      );
                                    return (
                                      <>
                                        {selectedCardIds.size > 0 && (
                                          <div className="gc-bulkbar">
                                            <span className="gc-bulkbar__count">
                                              {selectedCardIds.size} card
                                              {selectedCardIds.size === 1
                                                ? ""
                                                : "s"}{" "}
                                              selected
                                            </span>
                                            <div className="gc-actions">
                                              <button
                                                className="gc-btn gc-btn--ghost gc-btn--sm"
                                                onClick={() =>
                                                  setSelectedCardIds(new Set())
                                                }
                                              >
                                                Clear
                                              </button>
                                              <button
                                                className="gc-btn gc-btn--sm"
                                                onClick={() =>
                                                  setShowBulkActivate(true)
                                                }
                                              >
                                                Activate Selected
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                        <div className="gc-subtable gc-scroll">
                                          <table className="gc-table">
                                            <thead>
                                              <tr>
                                                <th style={{ width: "40px" }}>
                                                  <input
                                                    type="checkbox"
                                                    className="gc-checkbox"
                                                    aria-label="Select all activatable cards"
                                                    checked={allSelected}
                                                    disabled={
                                                      selectable.length === 0
                                                    }
                                                    onChange={toggleAllCards}
                                                  />
                                                </th>
                                                <th style={{ width: "210px" }}>
                                                  Card Number
                                                </th>
                                                <th style={{ width: "120px" }}>
                                                  SKU
                                                </th>
                                                <th style={{ width: "160px" }}>
                                                  Batch Code
                                                </th>
                                                <th style={{ width: "120px" }}>
                                                  Value
                                                </th>
                                                <th style={{ width: "120px" }}>
                                                  Balance
                                                </th>
                                                <th style={{ width: "130px" }}>
                                                  Status
                                                </th>
                                                <th style={{ width: "140px" }}>
                                                  Expiry
                                                </th>
                                                <th
                                                  style={{
                                                    width: "190px",
                                                    textAlign: "right",
                                                  }}
                                                />
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {cards.length > 0 ? (
                                                cards.map((card) => (
                                                  <tr
                                                    className="gc-row"
                                                    key={card?.id}
                                                  >
                                                    <td>
                                                      <input
                                                        type="checkbox"
                                                        className="gc-checkbox"
                                                        aria-label={`Select card ${card?.id}`}
                                                        checked={selectedCardIds.has(
                                                          card?.id
                                                        )}
                                                        disabled={
                                                          !ACTIVATABLE(
                                                            card?.status
                                                          )
                                                        }
                                                        title={
                                                          ACTIVATABLE(
                                                            card?.status
                                                          )
                                                            ? undefined
                                                            : `Already ${card?.status_display}`
                                                        }
                                                        onChange={() =>
                                                          toggleOneCard(
                                                            card?.id
                                                          )
                                                        }
                                                      />
                                                    </td>
                                                    <td className="gc-code">
                                                      {card?.formatted_number ||
                                                        card?.number}
                                                    </td>
                                                    <td className="gc-code">
                                                      {card?.sku}
                                                    </td>
                                                    <td className="gc-code">
                                                      {card?.batch_code || "—"}
                                                    </td>
                                                    <td className="gc-num">
                                                      {money(
                                                        card?.initial_amount
                                                      )}
                                                    </td>
                                                    <td className="gc-num gc-strong">
                                                      {money(card?.balance)}
                                                    </td>
                                                    <td>
                                                      <StatusPill
                                                        status={card?.status}
                                                        label={
                                                          card?.status_display
                                                        }
                                                      />
                                                    </td>
                                                    <td>
                                                      {card?.expiry_date ||
                                                        "No expiry"}
                                                    </td>
                                                    <td>
                                                      <div className="gc-cell-actions">
                                                        <button
                                                          className="gc-btn gc-btn--ghost gc-btn--sm"
                                                          onClick={() =>
                                                            history.push(
                                                              `/gift-card/card-detail/${card?.id}`
                                                            )
                                                          }
                                                        >
                                                          Details
                                                        </button>
                                                        {card?.status !== 2 &&
                                                          card?.status !== 4 && (
                                                            <button
                                                              className="gc-btn gc-btn--danger gc-btn--sm"
                                                              disabled={!canDelete}
                                                              title={
                                                                canDelete
                                                                  ? undefined
                                                                  : "Only Super Admin can delete a card"
                                                              }
                                                              onClick={() =>
                                                                setDeleteCardTarget(
                                                                  card
                                                                )
                                                              }
                                                            >
                                                              <Trash
                                                                size={15}
                                                                weight="bold"
                                                              />
                                                              Delete
                                                            </button>
                                                          )}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                ))
                                              ) : (
                                                <tr>
                                                  <td colSpan="9">
                                                    <div className="gc-empty">
                                                      <h4>
                                                        No cards in this batch
                                                      </h4>
                                                    </div>
                                                  </td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </>
                                    );
                                  })()}
                                  {batch?.generated_count > cards.length && (
                                    <p className="gc-note">
                                      Showing the first {cards.length} of{" "}
                                      {batch?.generated_count} cards — open “View
                                      Cards” for the full list.
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={COLUMNS}>
                    <div className="gc-empty">
                      <span className="gc-empty__icon">
                        <Stack size={24} />
                      </span>
                      <h4>No batches yet</h4>
                      <p>
                        Generate a batch to create gift cards with numbers, PINs
                        and barcodes.
                      </p>
                      <button
                        className="gc-btn gc-btn--sm"
                        onClick={() => setShowForm(true)}
                      >
                        <Plus size={15} weight="bold" />
                        Generate Cards
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {isLoading && (
          <div className="gc-loading">
            <Loader />
          </div>
        )}

        <GcPagination
          total={total}
          pageNumber={pageNumber}
          limit={LIMIT}
          prevUrl={prevUrl}
          nextUrl={nextUrl}
          isLoading={isLoading}
          onPrev={() => {
            loadUrl(prevUrl);
            setPageNumber((p) => Math.max(1, p - 1));
          }}
          onNext={() => {
            loadUrl(nextUrl);
            setPageNumber((p) => p + 1);
          }}
        />
      </div>

      <Modal
        centered
        dialogClassName="gc-modal"
        show={!!deleteTarget}
        onHide={() => !deleting && setDeleteTarget(null)}
      >
        <Modal.Header>
          <Modal.Title>Delete Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="gc-note">
            Are you sure you want to delete batch #{deleteTarget?.id} (
            {deleteTarget?.sku})?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="gc-btn gc-btn--ghost"
            disabled={deleting}
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </button>
          <button
            className="gc-btn gc-btn--danger"
            disabled={deleting}
            onClick={deleteBatch}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        dialogClassName="gc-modal"
        show={!!deleteCardTarget}
        onHide={() => !deletingCard && setDeleteCardTarget(null)}
      >
        <Modal.Header>
          <Modal.Title>Delete Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="gc-note">
            Are you sure you want to delete card{" "}
            {deleteCardTarget?.formatted_number || deleteCardTarget?.number}?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="gc-btn gc-btn--ghost"
            disabled={deletingCard}
            onClick={() => setDeleteCardTarget(null)}
          >
            Cancel
          </button>
          <button
            className="gc-btn gc-btn--danger"
            disabled={deletingCard}
            onClick={deleteCard}
          >
            {deletingCard ? "Deleting…" : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        size="lg"
        centered
        dialogClassName="gc-modal"
        show={showForm}
        onHide={() => !saving && setShowForm(false)}
      >
        <Modal.Header>
          <Modal.Title>Generate Gift Cards</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="gc-form-grid">
            <div>
              <label className="gc-label" htmlFor="gc-sku">
                SKU<span className="gc-req">*</span>
              </label>
              <Form.Control
                id="gc-sku"
                className="gc-input"
                name="sku"
                placeholder="GC-1000"
                value={form.sku}
                onChange={onChange}
              />
            </div>
            <div>
              <label className="gc-label" htmlFor="gc-amount">
                Amount<span className="gc-req">*</span>
              </label>
              <Form.Control
                id="gc-amount"
                className="gc-input"
                type="number"
                min={1}
                name="amount"
                placeholder="1000"
                value={form.amount}
                onChange={onChange}
              />
            </div>
            <div>
              <label className="gc-label" htmlFor="gc-selling-price">
                Selling Price
              </label>
              <Form.Control
                id="gc-selling-price"
                className="gc-input"
                type="number"
                min={1}
                name="selling_price"
                placeholder="900"
                value={form.selling_price}
                onChange={onChange}
              />
              <p className="gc-hint">Optional — what the customer pays.</p>
            </div>
            <div>
              <label className="gc-label" htmlFor="gc-qty">
                Quantity<span className="gc-req">*</span>
              </label>
              <Form.Control
                id="gc-qty"
                className="gc-input"
                type="number"
                min={1}
                name="quantity"
                placeholder="50"
                value={form.quantity}
                onChange={onChange}
              />
            </div>
            <div>
              <label className="gc-label" htmlFor="gc-prefix">
                Prefix
              </label>
              <Form.Control
                id="gc-prefix"
                className="gc-input"
                name="prefix"
                placeholder="KJ"
                value={form.prefix}
                onChange={onChange}
              />
              <p className="gc-hint">Optional — prepended to every number.</p>
            </div>
            <div className="gc-form-grid--full">
              <label className="gc-label" htmlFor="gc-note">
                Note
              </label>
              <Form.Control
                id="gc-note"
                className="gc-input"
                name="note"
                placeholder="Eid stock"
                value={form.note}
                onChange={onChange}
              />
              <p className="gc-hint">
                Cards are generated inactive with a random 6-digit PIN. A large
                batch takes a few seconds — each barcode is rendered and
                uploaded.
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="gc-btn gc-btn--ghost"
            disabled={saving}
            onClick={() => setShowForm(false)}
          >
            Cancel
          </button>
          <button className="gc-btn" disabled={saving} onClick={createBatch}>
            {saving ? "Generating…" : "Generate"}
          </button>
        </Modal.Footer>
      </Modal>

      <BulkActivateModal
        show={showBulkActivate}
        ids={[...selectedCardIds]}
        onHide={() => setShowBulkActivate(false)}
        onDone={() => {
          setSelectedCardIds(new Set());
          if (expandedId) reloadCards(expandedId);
        }}
      />
    </div>
  );
};

export default GiftCardBatchList;
